import { envClient } from '@nova/network';
import { BotPartSlot, CompletePartDataSet, PipelineSeriesByRegionColour } from '@nova/types';
import { Logger } from '@nova/utils';
import {
  ClientTextureGeneration,
  ClientTextureUtils,
  createRenderPipeline,
  IRenderPipeline,
  StaticPipelineOperations
} from '@nova/wimpackage';
import {
  AnimatedSprite,
  Application,
  Assets,
  Sprite,
  Spritesheet,
  Texture,
  TextureSource
} from 'pixi.js';

import { GameManager } from '../game/GameManager';
import { AnimUtils } from '../utils/anim-utils';

import { animationInfo, SpriteSheetMetaData } from './loaders/ExtendedSpriteSheetLoader';

type TextureGeneratorFunction = (...args: any[]) => Promise<unknown>;
type TextureGeneratorFunctionBatch = Map<string, TextureGeneratorFunction>;

interface ITextureGenerationResults {
  keys: string[];
}

export class AssetUtils {
  private static _generatorsToRun: TextureGeneratorFunctionBatch = new Map<string, TextureGeneratorFunction>();
  private static _batchRenderPipeline: IRenderPipeline | undefined;
  private static _standaloneApp: Application | undefined;
  private static readonly BASE_TARGET_TEXTURE_KEY: string = 'target_tile_area_fill_64';

  public static async initializeAssets(): Promise<void> {
    // eslint-disable-next-line dot-notation
    if (Assets['_initialized'] !== true) {
      await Assets.init({
        loadOptions: {
          strategy: 'retry',
          retryCount: 4,
          retryDelay: 400,
          onError: (err, asset) => Logger.debug('Failed To Load Asset:', asset)
        }
      });

      // Cursor's built-in browser only starts Web Workers once its DevTools are open, so worker texture loads
      // hang and scenes never leave the loading step. Local runs decode on the main thread instead.
      // Keyed on isLocal, not debugMode, since debug can also be on in deployed test envs.
      if (envClient.isLocal) {
        Assets.setPreferences({ preferWorkers: false });
      }
    }
  }

  /**
   * Sets a standalone app instance for use when GameManager is not initialized.
   *
   * @param app - The PIXI Application instance to use.
   */
  public static async setStandaloneApp(app: Application | undefined): Promise<void> {
    AssetUtils._standaloneApp = app;
    await AssetUtils.initializeAssets();
  }

  /**
   * Gets the active app instance (standalone or GameManager).
   *
   * @returns The active Application instance.
   */
  private static getApp(): Application {
    return this._standaloneApp ?? GameManager.app;
  }

  /**
   * Extracts the source URL of an asset.
   *
   * @param assetKey - The key of the asset.
   * @param options - Optional texture options.
   * @returns The source URL of the asset.
   */
  public static extractSourceURL(assetKey: string): string {
    return this.getApp().renderer.extract.canvas(Sprite.from(assetKey)).toDataURL?.('image/png') as string;
  }

  /**
   * Extracts the source URL of a frame from a sprite sheet.
   *
   * @param spriteSheetName - The name of the sprite sheet.
   * @param frameName - The name of the frame within the sprite sheet.
   * @returns The source URL of the frame.
   */
  public static extractSourceURLFromSpriteSheet(spriteSheetName: string, frameName: string): string {
    const spriteSheet = Assets.get<Spritesheet>(spriteSheetName);
    if (!spriteSheet) {
      throw new Error(`Sprite sheet "${spriteSheetName}" not found. Ensure it is loaded before calling this method.`);
    }

    const texture = spriteSheet.textures[frameName];
    if (!texture) {
      throw new Error(`Frame "${frameName}" not found in sprite sheet "${spriteSheetName}".`);
    }

    const sprite = new Sprite(texture);
    return GameManager.app.renderer.extract.canvas(sprite).toDataURL?.('image/png') as string;
  }

  /**
   * Begins a batch render operation.
   * This method clears the generators to run and creates a reusable render pipeline.
   */
  public static beginBatchRender(): void {
    this._generatorsToRun.clear();

    this._batchRenderPipeline = createRenderPipeline(0, 0);
    StaticPipelineOperations.setSharedPipeline(this._batchRenderPipeline);
  }

  /**
   * Runs the batch render process.
   * This method generates textures using the registered generator functions.
   * @returns A promise that resolves when the batch render is complete.
   */
  public static async runBatchRender(): Promise<void> {
    if (!this._batchRenderPipeline) {
      Logger.error('No render pipeline to run batch render with.');
      return;
    }

    const result: ITextureGenerationResults = { keys: [] };
    const loadPromises: Promise<unknown>[] = [];
    this._generatorsToRun.forEach((genFunc, key) => {
      loadPromises.push(genFunc.call(null));
      result.keys.push(key);
    });

    await Promise.all(loadPromises);

    this._generatorsToRun.clear();
    this._batchRenderPipeline.destroy();
    this._batchRenderPipeline = undefined;
    StaticPipelineOperations.setSharedPipeline(undefined);

    if (envClient.debugMode) {
      Logger.log(`Batch render complete. Generated ${result.keys.length} textures.`);
    }
  }

  private static isQueued(newTextureKey: string): boolean {
    return !!this._batchRenderPipeline && this._generatorsToRun.has(newTextureKey);
  }

  private static isLoadedOrQueued(key: string): boolean {
    return Assets.cache.has(key) || this.isQueued(key);
  }

  /**
   * Queues a new texture to be generated using the provided generator function.
   *
   * @param newTextureKey - The key for the new texture.
   * @param generator - The function that generates the texture.
   */
  private static queueTexture(newTextureKey: string, generator: TextureGeneratorFunction) {
    if (!this._batchRenderPipeline) {
      Logger.error('Batch Renderer not started for queue.');
      return;
    }

    if (this.isLoadedOrQueued(newTextureKey)) {
      return;
    }

    this._generatorsToRun.set(newTextureKey, generator);
  }

  /**
   * Queues a texture tinted by team ID.
   *
   * @param baseTextureKey - The key of the base texture.
   * @param teamId - The ID of the team.
   */
  public static queueTextureTintedByTeamID(baseTextureKey: string, teamId: number, teamColor: number[]) {
    const newTextureKey = AssetUtils.getCopiedAssetKeyName(baseTextureKey, 'teamTinted', teamId);
    const generator = this.loadTintedBaseTexture.bind(this, newTextureKey, baseTextureKey, teamColor);
    this.queueTexture(newTextureKey, generator);
  }

  /**
   * Queues a texture shaded by a swatch for loading.
   *
   * @param baseTextureKey - The key of the base texture.
   * @param maskTextureKey - The key of the mask texture.
   * @param swatchId - The ID of the swatch.
   */
  public static queueTextureShadedBySwatch(baseTextureKey: string, maskTextureKey: string, swatchId: string, swatchData: PipelineSeriesByRegionColour) {
    if (Object.keys(swatchData).length === 0) {
      Logger.warn(
        `AssetUtils::queueTextureShadedBySwatch: Empty swatch data array passed in for base texture <${baseTextureKey}>, ` +
        `mask texture <${maskTextureKey}>, swatch <${swatchId}>.`
      );
      return;
    }

    const newTextureKey = this.getSwatchAssetKeyName(baseTextureKey, swatchId);
    const generator = this.loadSwatchShadedBaseTexture.bind(this, newTextureKey, baseTextureKey, maskTextureKey, swatchData);
    this.queueTexture(newTextureKey, generator);
  }

  /**
   * Queues a skill target texture for loading.
   *
   * @param width - The width of the texture.
   * @param height - The height of the texture.
   */
  public static queueSkillTargetTexture(width: number, height: number) {
    const roundedWidth = Math.round(width);
    const roundedHeight = Math.round(height);
    const newTextureKey = this.getSkillTargetTextureKey(width, height);
    const generator = this.loadTargetTexture.bind(this, newTextureKey, roundedWidth, roundedHeight);
    this.queueTexture(newTextureKey, generator);
  }

  /**
   * Loads a tinted base texture from the given source and applies the specified colour.
   *
   * @param source The source of the texture.
   * @param alias The alias for the texture to save to the cache.
   * @param colour The colour to apply to the texture.
   * @returns A promise that resolves to the loaded base texture.
   */
  public static async loadTintedBaseTexture(alias: string, source: string, colour: number[]): Promise<TextureSource> {
    return this.loadCustomTexture(alias, async () => {
      const sourceImage: HTMLImageElement = await this.getTextureSource(source);
      const newTexture: HTMLImageElement = ClientTextureGeneration.CreateTinted(sourceImage, colour);
      return newTexture.src;
    });
  }

  /**
   * Loads a swatch shaded base texture.
   *
   * @param alias - The alias for the texture to save to the cache.
   * @param baseTexture - The path to the base texture.
   * @param maskTexture - The path to the mask texture.
   * @param swatchData - The swatch data for generating the new texture.
   * @returns A promise that resolves to the loaded base texture.
   */
  public static async loadSwatchShadedBaseTexture(
    alias: string, baseTexture: string, maskTexture: string, swatchData: PipelineSeriesByRegionColour): Promise<TextureSource> {
    return this.loadCustomTexture(alias, async () => {
      const baseSourceImage: HTMLImageElement = await this.getTextureSource(baseTexture);
      const maskSourceImage: HTMLImageElement = await this.getTextureSource(maskTexture);
      const newTexture: HTMLImageElement = ClientTextureGeneration.CreateFromPipelineSeriesByRegionColour(
        baseSourceImage,
        maskSourceImage,
        swatchData);

      return newTexture.src;
    });
  }

  public static async loadTargetTexture(alias: string, roundedWidth: number, roundedHeight: number): Promise<TextureSource> {
    return this.loadCustomTexture(alias, async () => {
      const baseSourceImage = await this.getTextureSource(this.BASE_TARGET_TEXTURE_KEY);
      const targetTileImageData = ClientTextureUtils.GetImageDataFromImage(baseSourceImage);
      const newTexture: HTMLImageElement = ClientTextureGeneration.CreateTiled(
        targetTileImageData,
        roundedWidth,
        roundedHeight);

      return newTexture.src;
    });
  }

  /**
   * Loads a custom texture with the given alias and creates a new texture if it doesn't exist in the cache.
   *
   * @param alias - The alias for the texture to save to the cache.
   * @param createNewTexture - A function that returns a Promise resolving to the path of the new texture.
   * @returns A Promise that resolves to the base texture of the loaded or created texture.
   */
  private static async loadCustomTexture(alias: string, createNewTexture: () => Promise<string>): Promise<TextureSource> {
    const hasCachedTexture = Assets.cache.has(alias);
    if (hasCachedTexture) {
      return Assets.get<Texture>(alias).source;
    }
    const newTexture = await Assets.load<Texture>({ src: await createNewTexture(), alias });
    return newTexture.source;
  }

  /**
   * Retrieves the texture source for the given asset source.
   *
   * @param source - The asset source.
   * @returns A promise that resolves to the texture source as an HTMLImageElement.
   */
  public static async getTextureSource(source: string | Texture): Promise<HTMLImageElement> {
    const originalTexture: Texture = (source instanceof Texture) ? source : await Assets.load(source);
    const originalSource = await this.getApp().renderer.extract.image(originalTexture) as HTMLImageElement;

    // Wait for the image to fully load if it hasn't already
    if (originalSource.naturalWidth === 0 || originalSource.naturalHeight === 0) {
      await new Promise<void>((resolve, reject) => {
        originalSource.onload = () => resolve();
        originalSource.onerror = () => reject(new Error('Failed to load extracted image'));
      });
    }

    // Forcefully add naturalHeight and naturalWidth properties if they don't exist
    if (!('naturalHeight' in originalSource)) {
      const height = (originalSource as HTMLImageElement).height;
      Object.defineProperty(originalSource, 'naturalHeight', {
        get: () => height,
        configurable: true
      });
    }

    if (!('naturalWidth' in originalSource)) {
      const width = (originalSource as HTMLImageElement).width;
      Object.defineProperty(originalSource, 'naturalWidth', {
        get: () => width,
        configurable: true
      });
    }

    return originalSource;
  }

  /**
   * Creates a unique key name for a copied asset.
   *
   * @param originalName - The original name of the asset.
   * @param copyContextId - The ID of the copy context.
   * @param copyContextIndex - The optional index of the copy context.
   * @returns The unique key name for the copied asset.
   */
  public static getCopiedAssetKeyName(originalName: string, copyContextId: string, copyContextIndex?: number): string {
    return `${copyContextId}${copyContextIndex !== undefined ? `_${copyContextIndex}` : ''}_${originalName}`;
  }

  public static getSwatchAssetKeyName(baseTextureKey: string, swatchId: string): string {
    return `${baseTextureKey}.${swatchId}`;
  }

  public static getBotAtlasKey(
    parts: CompletePartDataSet,
    options?: { teamColorName?: string; visibleSlots?: BotPartSlot[] }
  ): string {
    let key = 'bot.';
    const visibleSlotSet = options?.visibleSlots?.length ? new Set(options.visibleSlots) : undefined;
    const sortedKeys = Object.keys(parts).sort();
    for (const partKeyStr of sortedKeys) {
      const partKey = partKeyStr as BotPartSlot;
      if (visibleSlotSet && !visibleSlotSet.has(partKey)) {
        continue;
      }
      const part = parts[partKey];
      key += `${part.datasetKey}.${part.swatchKey}.`;
    }

    if (options?.teamColorName) {
      key += `teamColor.${options.teamColorName}.`;
    }

    if (visibleSlotSet) {
      key += `visible.${[...visibleSlotSet].sort().join('.')}.`;
    }

    return key;
  }

  public static getTeamColorTintAssetKeyName(baseTextureKey: string, teamColorName: string): string {
    return `${baseTextureKey}.teamColorTint.${teamColorName}`;
  }

  /**
   * Applies an RGB tint multiplier to an existing texture and caches the result.
   * When maskKey is provided, tints only pixels outside swatch mask regions.
   */
  public static async tintTeamColor(
    alias: string,
    source: TextureSource | Texture,
    colour: number[],
    maskKey?: string
  ): Promise<TextureSource> {
    return this.loadCustomTexture(alias, async () => {
      const texture = source instanceof Texture ? source : Texture.from(source);
      const sourceImage: HTMLImageElement = await this.getTextureSource(texture);
      const maskImage = maskKey ? await this.getTextureSource(maskKey) : undefined;
      const newTexture: HTMLImageElement = ClientTextureGeneration.applyTintToUnmaskedRegions(sourceImage, colour, maskImage);
      return newTexture.src;
    });
  }

  public static getSkillTargetTextureKey(width: number, height: number): string {
    const roundedWidth = Math.round(width);
    const roundedHeight = Math.round(height);
    return this.getCopiedAssetKeyName(this.BASE_TARGET_TEXTURE_KEY, `tiled_${roundedWidth}x${roundedHeight}`);
  }

  /**
   * Resolves animation frames from a sprite sheet.
   * Prefers Pixi's `animations` map, then TF clips under `data.meta.clips`.
   *
   * @param spriteSheet - The sprite sheet containing the animations.
   * @param animationName - The name of the animation or clip.
   * @param reverse - Whether to reverse the frame order.
   * @returns The frame textures, or undefined when neither source defines the clip.
   */
  public static getAnimationFramesFromSpriteSheet(spriteSheet: Spritesheet, animationName: string, reverse = false): Texture[] | undefined {
    const pixiFrames = spriteSheet.animations?.[animationName];
    if (pixiFrames) {
      return reverse ? pixiFrames.slice().reverse() : pixiFrames;
    }

    const animationData = (spriteSheet.data as SpriteSheetMetaData).meta?.clips?.[animationName];
    if (!animationData) { return undefined; }

    const frames = this.generateFramesForAnimation(spriteSheet, animationData);
    return reverse ? frames.reverse() : frames;
  }

  /**
   * Creates an animated sprite from a sprite sheet.
   *
   * @param spriteSheet - The sprite sheet containing the animations.
   * @param animationName - The name of the animation to create.
   * @param reverse - Whether to reverse the animation frames.
   * @returns The created AnimatedSprite.
   */
  public static createAnimationFromSpriteSheet(spriteSheet: Spritesheet, animationName: string, reverse = false): AnimatedSprite {
    if (spriteSheet.animations?.[animationName]) {
      const animation = new AnimatedSprite(spriteSheet.animations[animationName]);
      animation.loop = false;
      return animation;
    }

    const animationData = (spriteSheet.data as SpriteSheetMetaData).meta.clips[animationName];
    const frames = this.getAnimationFramesFromSpriteSheet(spriteSheet, animationName, reverse) ?? this.generateFramesForAnimation(spriteSheet, animationData);
    const animation = new AnimatedSprite(frames);
    animation.loop = animationData.repeat === -1;
    animation.animationSpeed = AnimUtils.BASE_ANIMATION_SCALER;
    return animation;
  }

  /**
   * Generates an array of frames for an animation based on the provided sprite sheet and animation data.
   *
   * @param spriteSheet - The sprite sheet containing the textures for the animation.
   * @param animationData - The animation data specifying the start frame, end frame, and prefix for the animation.
   * @returns An array of textures representing the frames of the animation.
   */
  public static generateFramesForAnimation(spriteSheet: Spritesheet, animationData: animationInfo): Texture[] {
    const frames = [] as Texture[];
    const startFrame = animationData.start;
    const endFrame = animationData.end;
    for (let i = startFrame; i < endFrame + 1; i++) {
      const val = i.toString().padStart(4, '0');
      frames.push(spriteSheet.textures[`${animationData.prefix}${val}`]);
    }

    return frames;
  }

  /**
   * Creates a sprite from a sprite sheet.
   *
   * @param spriteSheetName - The name of the sprite sheet.
   * @param frameName - The name of the frame within the sprite sheet.
   * @returns The created sprite.
   */
  public static createSpriteFromSpriteSheet(spriteSheetName: string, frameName: string): Sprite {
    // Try to load the sprite first
    let sprite = Sprite.from(frameName);

    // Try to load from spritesheet
    if (!sprite) {
      const spriteSheet = Assets.get<Spritesheet>(spriteSheetName);
      sprite = new Sprite(spriteSheet.textures[frameName]);
    }
    return sprite;
  }

  /**
   * Returns the suffix of a file name.
   *
   * @param fileName - The name of the file.
   * @returns The suffix of the file name.
   */
  public static getSuffix(fileName: string): string {
    const parts = fileName.split('.');
    return parts.pop() as string;
  }
}
